// src/components/course/pages/CourseDiscovery.jsx
import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Star, Clock, Users, ChevronDown, BookOpen,
  Grid3x3, List, Heart, Send, X, ShoppingCart, CheckCircle, TrendingUp
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import { wishlistService } from '../../../services/wishlistService';

const CourseDiscovery = ({ bookId, user, customStyling, onNavigate }) => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [wishlist, setWishlist] = useState([]);
  const [showWishlistSidebar, setShowWishlistSidebar] = useState(false);

  const categories = [
    'Web Development', 'Data Science', 'Design', 'Marketing', 
    'Business', 'Personal Development', 'Languages', 'Other'
  ];

  const levels = ['Beginner', 'Intermediate', 'Advanced'];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'title', label: 'A-Z' }
  ];

  useEffect(() => {
    loadCourses();
    if (user) loadWishlist();
  }, [bookId, user]);

  useEffect(() => {
    filterAndSortCourses();
  }, [courses, searchTerm, selectedCategory, selectedLevel, sortBy]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const allCourses = await courseService.getCourses(bookId, { isPublished: true });
      setCourses(allCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWishlist = async () => {
    try {
      const userWishlist = await wishlistService.getWishlist(bookId, user.uid);
      setWishlist(userWishlist);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const filterAndSortCourses = () => {
    let filtered = [...courses];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(course => 
        course.title?.toLowerCase().includes(searchLower) ||
        course.description?.toLowerCase().includes(searchLower) ||
        course.category?.toLowerCase().includes(searchLower) ||
        course.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    if (selectedLevel) {
      filtered = filtered.filter(course => course.level === selectedLevel);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return (b.enrollmentCount || 0) - (a.enrollmentCount || 0);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'newest':
        default:
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }
    });

    setFilteredCourses(filtered);
  };

  const toggleWishlist = async (courseId, e) => {
    e.stopPropagation();
    if (!user) {
      alert('Please sign in to add courses to wishlist');
      return;
    }

    try {
      const isInWishlist = wishlist.some(item => item.courseId === courseId);
      if (isInWishlist) {
        await wishlistService.removeFromWishlist(bookId, user.uid, courseId);
        setWishlist(prev => prev.filter(item => item.courseId !== courseId));
      } else {
        await wishlistService.addToWishlist(bookId, user.uid, courseId);
        const course = courses.find(c => c.id === courseId);
        setWishlist(prev => [...prev, { courseId, course }]);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedLevel('');
    setSortBy('newest');
  };

  const isInWishlist = (courseId) => wishlist.some(item => item.courseId === courseId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 overflow-hidded">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Discover Courses</h1>
              <p className="text-gray-600">Explore our catalog of courses</p>
            </div>
            {user && (
              <button
                onClick={() => setShowWishlistSidebar(!showWishlistSidebar)}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:bg-blue-700 relative"
                style={{ backgroundColor: customStyling.primaryColor }}
              >
                <Heart className={`w-5 h-5 ${wishlist.length > 0 ? 'fill-white' : ''}`} />
                <span>Wishlist</span>
                {wishlist.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center">
                    {wishlist.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Search and Filters Bar */}
          <div className="mb-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-3 border rounded-lg hover:bg-gray-50 ${
                    showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300'
                  }`}
                >
                  <Filter className="w-5 h-5" />
                  <span className="hidden sm:inline">Filters</span>
                </button>

                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-3 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'}`}
                  >
                    <Grid3x3 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-3 border-l border-gray-300 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'}`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
                  <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Levels</option>
                    {levels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              {filteredCourses.length} {filteredCourses.length === 1 ? 'course' : 'courses'} found
            </p>
          </div>

          {/* Courses Display */}
          {filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No courses found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your filters or search terms</p>
              <button
                onClick={clearFilters}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear all filters
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCourses.map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onNavigate={onNavigate}
                  onToggleWishlist={toggleWishlist}
                  isInWishlist={isInWishlist(course.id)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCourses.map(course => (
                <CourseListItem
                  key={course.id}
                  course={course}
                  onNavigate={onNavigate}
                  onToggleWishlist={toggleWishlist}
                  isInWishlist={isInWishlist(course.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Wishlist Sidebar */}
      {showWishlistSidebar && (
        <WishlistSidebar
          bookId={bookId}
          user={user}
          wishlist={wishlist}
          courses={courses}
          onClose={() => setShowWishlistSidebar(false)}
          onRemove={toggleWishlist}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
};

// Course Card Component (Grid View)
const CourseCard = ({ course, onNavigate, onToggleWishlist, isInWishlist }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group relative">
    <button
      onClick={() => onNavigate('course', { courseId: course.id })}
      className="w-full text-left"
    >
      <div className="aspect-video bg-gradient-to-br from-blue-400 to-purple-500 relative overflow-hidden">
        {course.thumbnail ? (
          <img 
            src={course.thumbnail.url} 
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-white opacity-80" />
          </div>
        )}
        
        {course.level && (
          <div className="absolute top-3 right-3 bg-white bg-opacity-90 px-2 py-1 rounded text-xs font-medium">
            {course.level}
          </div>
        )}
      </div>

      <div className="p-4">
        {course.category && (
          <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
            {course.category}
          </span>
        )}

        <h3 className="font-semibold text-gray-900 mt-2 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {course.title}
        </h3>

        {course.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {course.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          {course.lessonCount && (
            <div className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              <span>{course.lessonCount} lessons</span>
            </div>
          )}
          {course.duration && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{course.duration}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-medium text-gray-900">
              {course.rating ? course.rating.toFixed(1) : 'New'}
            </span>
            {course.ratingCount > 0 && (
              <span className="text-xs text-gray-500">({course.ratingCount})</span>
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Users className="w-4 h-4" />
            <span>{course.enrollmentCount || 0}</span>
          </div>
        </div>

        {course.price !== undefined && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            {course.price === 0 ? (
              <span className="text-green-600 font-semibold">Free</span>
            ) : (
              <span className="text-gray-900 font-semibold">R{course.price}</span>
            )}
          </div>
        )}
      </div>
    </button>

    <button
      onClick={(e) => onToggleWishlist(course.id, e)}
      className="absolute top-48 right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 z-10"
    >
      <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
    </button>
  </div>
);

// Course List Item Component (List View)
const CourseListItem = ({ course, onNavigate, onToggleWishlist, isInWishlist }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group">
    <button
      onClick={() => onNavigate('course', { courseId: course.id })}
      className="w-full text-left"
    >
      <div className="flex flex-col sm:flex-row gap-4 p-4">
        <div className="w-full sm:w-48 h-32 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg overflow-hidden flex-shrink-0">
          {course.thumbnail ? (
            <img 
              src={course.thumbnail.url} 
              alt={course.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-white opacity-80" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              {course.category && (
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                  {course.category}
                </span>
              )}
              
              <h3 className="font-semibold text-gray-900 mt-1 mb-2 group-hover:text-blue-600 transition-colors">
                {course.title}
              </h3>

              {course.description && (
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {course.description}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                {course.level && (
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                    {course.level}
                  </span>
                )}
                {course.lessonCount && (
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    <span>{course.lessonCount} lessons</span>
                  </div>
                )}
                {course.duration && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{course.duration}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="font-medium text-gray-900">
                    {course.rating ? course.rating.toFixed(1) : 'New'}
                  </span>
                  {course.ratingCount > 0 && (
                    <span className="text-xs">({course.ratingCount})</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{course.enrollmentCount || 0} enrolled</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              {course.price !== undefined && (
                <div>
                  {course.price === 0 ? (
                    <span className="text-green-600 font-semibold">Free</span>
                  ) : (
                    <span className="text-gray-900 font-semibold text-lg">R{course.price}</span>
                  )}
                </div>
              )}
              
              <button
                onClick={(e) => onToggleWishlist(course.id, e)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </button>
  </div>
);

// Wishlist Sidebar Component
const WishlistSidebar = ({ bookId, user, wishlist, courses, onClose, onRemove, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('wishlist');
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const wishlistCourses = wishlist.map(item => {
    const course = courses.find(c => c.id === item.courseId);
    return { ...item, course };
  }).filter(item => item.course);

  const handleSubmitQuery = async () => {
    if (!query.trim() || wishlist.length === 0) return;

    try {
      setSending(true);
      await wishlistService.submitWishlistQuery(bookId, user.uid, wishlist, query);
      alert('Your wishlist query has been sent successfully!');
      setQuery('');
    } catch (error) {
      console.error('Error submitting query:', error);
      alert('Failed to send query. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      setEnrolling(true);
      const course = courses.find(c => c.id === courseId);
      await courseService.enrollUser(user.uid, bookId, courseId, course?.isPremium ? 'premium' : 'free');
      alert('Successfully enrolled in course!');
      onNavigate('course', { courseId });
    } catch (error) {
      console.error('Error enrolling:', error);
      alert('Failed to enroll. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="w-96 bg-white border-l border-gray-200 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Wishlist</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('wishlist')}
            className={`flex-1 px-4 py-2 font-medium transition-colors ${
              activeTab === 'wishlist'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Heart className="w-4 h-4" />
              <span>Wishlist ({wishlist.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('enroll')}
            className={`flex-1 px-4 py-2 font-medium transition-colors ${
              activeTab === 'enroll'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              <span>Enroll</span>
            </div>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'wishlist' ? (
          <div className="space-y-4">
            {wishlistCourses.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">Your wishlist is empty</p>
                <p className="text-sm text-gray-500 mt-1">Add courses to get started</p>
              </div>
            ) : (
              <>
                {wishlistCourses.map(item => (
                  <div key={item.courseId} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                    <div className="flex gap-3">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded flex-shrink-0 overflow-hidden">
                        {item.course.thumbnail ? (
                          <img src={item.course.thumbnail.url} alt={item.course.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
                          {item.course.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          {item.course.lessonCount && (
                            <span>{item.course.lessonCount} lessons</span>
                          )}
                          {item.course.price !== undefined && (
                            <span className="font-medium text-gray-900">
                              {item.course.price === 0 ? 'Free' : `R${item.course.price}`}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => onNavigate('course', { courseId: item.courseId })}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            View Details
                          </button>
                          <button
                            onClick={(e) => onRemove(item.courseId, e)}
                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Send Query to Provider
                  </label>
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Ask about courses, pricing, schedules, or request more information..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    onClick={handleSubmitQuery}
                    disabled={sending || !query.trim()}
                    className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    <span>{sending ? 'Sending...' : 'Send Query'}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Select a course from your wishlist to enroll
            </p>
            
            {wishlistCourses.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No courses to enroll in</p>
                <p className="text-sm text-gray-500 mt-1">Add courses to your wishlist first</p>
              </div>
            ) : (
              wishlistCourses.map(item => (
                <div key={item.courseId} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex gap-3 mb-3">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded flex-shrink-0 overflow-hidden">
                      {item.course.thumbnail ? (
                        <img src={item.course.thumbnail.url} alt={item.course.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm mb-1">
                        {item.course.title}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {item.course.price !== undefined && (
                          <span className="font-semibold text-gray-900">
                            {item.course.price === 0 ? 'Free Course' : `R${item.course.price}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleEnroll(item.courseId)}
                    disabled={enrolling}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>{enrolling ? 'Enrolling...' : 'Enroll Now'}</span>
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDiscovery;